import clsx from 'clsx'
import _ from 'lodash'

import { truncateName } from '@/shared/lib/string/truncate-name'
import AppImage from '@/shared/ui/AppImage'
import { Avatar } from '@/shared/ui/Avatar'
import { GameIcon } from '@/shared/ui/GameIcon'

import type { RankingEntry } from '../hooks/useRankingData'

type PodiumStep = {
  ring: string
  block: string
  height: string
  avatar: string
}

// Índice = rank − 1. Centro (1º) mais alto, prata à esquerda, bronze à direita.
const PODIUM_STEPS: PodiumStep[] = [
  {
    ring: 'ring-[#fcd34d]',
    block: 'bg-[linear-gradient(180deg,#fcd34d_0%,#f59e0b_100%)]',
    height: 'h-16',
    avatar: 'h-14 w-14'
  },
  {
    ring: 'ring-[#94a3b8]',
    block: 'bg-[linear-gradient(180deg,#cbd5e1_0%,#94a3b8_100%)]',
    height: 'h-11',
    avatar: 'h-11 w-11'
  },
  {
    ring: 'ring-[#fb923c]',
    block: 'bg-[linear-gradient(180deg,#fdba74_0%,#fb923c_100%)]',
    height: 'h-8',
    avatar: 'h-10 w-10'
  }
]

const PodiumColumn = ({ entry }: { entry: RankingEntry }) => {
  const step = PODIUM_STEPS[entry.rank - 1] ?? PODIUM_STEPS[2]
  const user = entry.user_progress?.user
  const avatarConfig = entry.user_progress?.avatar

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1">
      <span
        className={clsx(
          'block overflow-hidden rounded-full bg-white ring-[2.5px]',
          step.ring,
          step.avatar
        )}
      >
        {avatarConfig && !_.isEmpty(avatarConfig) ? (
          <Avatar config={avatarConfig} showFullBody={false} className="" />
        ) : (
          <AppImage
            src={_.get(user, 'avatar.file.url', '/placeholders/user.svg')}
            width={112}
            height={112}
            className="h-full w-full object-cover object-center"
            alt={user?.name || ''}
          />
        )}
      </span>

      <span className="line-clamp-1 max-w-full px-1 text-center text-2xs font-bold leading-tight text-gray-900">
        {_.startCase(_.toLower(truncateName(user?.name || '', 14)))}
        {entry.me && <span className="text-gray-500"> (eu)</span>}
      </span>

      <span className="flex items-center gap-1 text-2xs font-semibold leading-none text-amber-500">
        <GameIcon name="star" size={10} />
        {entry.points} XP
      </span>

      <div
        className={clsx(
          'flex w-full items-start justify-center rounded-t-[0.625rem] pt-1',
          step.block,
          step.height
        )}
      >
        <span className="text-sm font-extrabold leading-none text-white drop-shadow-sm">
          {entry.rank}
        </span>
      </div>
    </div>
  )
}

type RankingPodiumProps = {
  top3: RankingEntry[]
  className?: string
}

export const RankingPodium = ({ top3, className }: RankingPodiumProps) => {
  if (!top3.length) return null

  // Ordem visual prata · ouro · bronze (1º ao centro)
  const ordered = [top3[1], top3[0], top3[2]].filter(Boolean) as RankingEntry[]

  return (
    <div className={clsx('flex items-end gap-2', className)}>
      {ordered.map(entry => (
        <PodiumColumn key={entry.id} entry={entry} />
      ))}
    </div>
  )
}
